// SectionToggle — client-side accordion toggle for settings sections.
//
// Replaces the server-side `toggle_section` round-trip for plain sections.
// Sections that need server side-effects (opening modals, lazy-loading data)
// set `data-section-notify="<section-name>"` on the wrapper; the hook will
// push a `toggle_section` event to the server after every toggle.
//
// Markup convention:
//   Wrapper:               phx-hook="sectionToggle", id="...",
//                          optional data-section-notify="<section_atom>"
//   Toggle trigger button: [data-section-header], data-open="true|false"
//   Collapsible body:      [data-section-body] — toggled via "hidden" class
//
// The hook element is the wrapper CONTAINING header and body, so a
// LiveView patch of the section re-renders the wrapper and fires
// `updated()`; state re-assertion there keeps a patch from snapping
// an open section shut. State encoding is the shared `_panelOpen`
// convention (./panel_state).

import type { LiveViewHook } from "./types";
import type { PanelState } from "./panel_state";

const applyState = (el: HTMLElement, open: boolean): void => {
  const body = el.querySelector<HTMLElement>("[data-section-body]");
  const btn = el.querySelector<HTMLElement>("[data-section-header]");
  if (!body) return;
  body.classList.toggle("hidden", !open);
  if (btn) {
    btn.dataset["open"] = String(open);
  }
};

export interface SectionToggleState extends PanelState {
  _onClick: (e: Event) => void;
}

export const SectionToggle = {
  mounted(this: LiveViewHook<never, string> & SectionToggleState) {
    const notifySection = this.el.dataset["sectionNotify"] ?? null;

    const body = this.el.querySelector<HTMLElement>("[data-section-body]");
    this._panelOpen = body ? !body.classList.contains("hidden") : false;

    const onClick = (e: Event): void => {
      const btn = (e.target as HTMLElement).closest<HTMLElement>(
        "[data-section-header]",
      );
      if (!btn) return;
      const body = this.el.querySelector<HTMLElement>("[data-section-body]");
      if (!body) return;
      this._panelOpen = !this._panelOpen;
      applyState(this.el, this._panelOpen);

      // Notify server for sections that need side-effects or server-side
      // open/closed state. The event fires on both expand and collapse so
      // a later LiveView patch cannot resurrect stale server state.
      if (notifySection !== null) {
        this.pushEvent("toggle_section", { section: notifySection });
      }
    };
    this.el.addEventListener("click", onClick);
    this._onClick = onClick;
  },

  updated(this: LiveViewHook<never, string> & SectionToggleState) {
    // The wrapper was patched from server state — re-assert what the
    // user actually chose.
    applyState(this.el, this._panelOpen);
  },

  destroyed(this: LiveViewHook<never, string> & SectionToggleState) {
    this.el.removeEventListener("click", this._onClick as EventListener);
  },
};
